import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePostAnswersMutation } from '../api/usePostAnswersMutation';
import { Answers, CurrentQuestion } from './Survey.meta';
import { getQueryStringParam } from '../../CommonUtils/getQueryStringParam';
import { useSurveyQuery } from '../api/useSurveyQuery';
import { useBootstrapConsumerArea } from '../../ConsumerArea/hooks/useBootstrapConsumerArea';
import { useClientId } from '../../_hooks/useClientId';
import {
  isConsumerArea as selectIsConsumerArea,
  selectOnboardingWorkflow,
} from '../../CommonSelectors/CommonAreaSelector';
import { OnboardingWorkflow } from '../../CommonSelectors/CommonArea.types';
import { toast } from '../../CommonComponents/OPL/Toast/Toast';

export const useSurvey = () => {
  const skipStartPage = getQueryStringParam('skipStartPage') === 'true';

  const [currentQuestion, setCurrentQuestion] = useState<CurrentQuestion>({
    index: skipStartPage ? 0 : -1,
    direction: 'forward',
  });

  const queryInfo = useSurveyQuery();

  const questions = queryInfo.data?.questions ?? [];

  const [answers, setAnswers] = useState<Answers>({});

  const [isMovingForward, setIsMovingForward] = useState(false);
  const [isMovingBackwards, setIsMovingBackwards] = useState(false);

  const postAnswersMutationBag = usePostAnswersMutation();

  const { mutateAsync: bootstrapConsumerArea } = useBootstrapConsumerArea();

  const clientId = useClientId();

  const isConsumerArea = useSelector(selectIsConsumerArea);

  const onboardingWorkflow = useSelector(selectOnboardingWorkflow);

  const selectAnswer = (questionIndex: number, answer: number | null) => {
    setAnswers({ ...answers, [questionIndex]: answer });
  };

  const handlePostAnswers = async () => {
    if (!queryInfo.data?.surveyId) {
      return;
    }

    try {
      const { compareToSurveyId } = await postAnswersMutationBag.mutateAsync({ answers });

      if (isConsumerArea && onboardingWorkflow !== OnboardingWorkflow.None) {
        await bootstrapConsumerArea({
          clientId: clientId.toString(),
          completedWorkflow: true,
        });
      }

      ADV.util.Router.go2('BeFi20Results', {
        clientId,
        compareToId: compareToSurveyId || '',
        isFromSurvey: true,
        isInitiallyComparing: false,
      });
    } catch (err) {
      console.error(err);

      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleOnAnswer = (questionIndex: number, answer: number) => {
    if (isMovingForward || isMovingBackwards) {
      return;
    }

    selectAnswer(questionIndex, answer);

    setIsMovingForward(true);
  };

  const handleOnBack = (currentQuestionIndex: number) => {
    if (isMovingForward || isMovingBackwards) {
      return;
    }

    if (currentQuestionIndex === 0) {
      if (skipStartPage) {
        window.history.back();

        return;
      }

      setCurrentQuestion({ index: -1, direction: 'forward' });
      setAnswers({});
      return;
    }

    setIsMovingBackwards(true);

    setCurrentQuestion({ index: currentQuestionIndex - 1, direction: 'backward' });
  };

  const moveForwardTimeoutCallback = () => {
    if (currentQuestion.index + 1 < questions.length) {
      setCurrentQuestion({ index: currentQuestion.index + 1, direction: 'forward' });
    } else {
      handlePostAnswers();
    }

    setIsMovingForward(false);
  };

  const moveBackwardsTimeoutCallback = () => {
    selectAnswer(currentQuestion.index, null);

    setIsMovingBackwards(false);
  };

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    return {
      ...queryInfo.data,
      currentQuestion,
      questions,
      answers,
      postAnswersMutationBag,
      isMovingForward,
      isMovingBackwards,
    };
  }, [
    answers,
    currentQuestion,
    isMovingBackwards,
    isMovingForward,
    postAnswersMutationBag,
    queryInfo.data,
    questions,
  ]);

  return {
    ...queryInfo,
    data,
    actions: {
      handleOnAnswer,
      handleOnBack,
      setCurrentQuestion,
      moveForwardTimeoutCallback,
      moveBackwardsTimeoutCallback,
    },
  };
};
